import React, { useState, useCallback, useEffect, useRef } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Alert,
  CircularProgress,
  Paper,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  MenuItem,
  Chip,
  IconButton,
  Tooltip,
  Collapse,
  Divider,
  Select,
  FormControl,
  InputLabel,
  SelectChangeEvent,
} from '@mui/material';
import InventoryIcon from '@mui/icons-material/Inventory';
import DownloadIcon from '@mui/icons-material/Download';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import FilterListIcon from '@mui/icons-material/FilterList';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ViewListIcon from '@mui/icons-material/ViewList';
import ErrorIcon from '@mui/icons-material/Error';

// ─── Types ───────────────────────────────────────────────────────────────────

interface EnrolledRow {
  vin: string;
  platform: string;
  make: string;
  model_name: string;
  model_year: string;
  color: string;
  country: string;
  vehicle_status: string;
  enrollment_status: string;
  enrollment_eligibility: string;
  package: string;
  package_status: string;
  connection_capability: string;
  telematics_available: string;
  services_activated: string;
  deleted_flag: string;
  source_of_delete: string;
  ordered_date: string;
  enrollment_date: string;
  defleeted_date: string;
  last_updated: string;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function StatusChip({ value }: { value: string }) {
  const colors: Record<string, 'success' | 'error' | 'warning' | 'info' | 'default'> = {
    ACTIVE: 'success',
    ENROLLED: 'success',
    DELIVERED: 'info',
    NOT_ENROLLED: 'warning',
    DELETED: 'error',
    true: 'error',
    false: 'success',
  };
  return (
    <Chip
      label={value || 'N/A'}
      size="small"
      color={colors[value] || 'default'}
      variant="outlined"
      sx={{ fontWeight: 500, fontSize: '0.75rem' }}
    />
  );
}

function SummaryCard({
  title,
  value,
  subtitle,
  color,
}: {
  title: string;
  value: number | string;
  subtitle?: string;
  color?: string;
}) {
  return (
    <Card
      sx={{
        minWidth: 140,
        flex: '1 1 140px',
        border: '1px solid',
        borderColor: 'divider',
      }}
    >
      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
        <Typography variant="overline" color="text.secondary">
          {title}
        </Typography>
        <Typography
          variant="h5"
          sx={{ fontWeight: 700, color: color || 'text.primary', mt: 0.5 }}
        >
          {typeof value === 'number' ? value.toLocaleString() : value}
        </Typography>
        {subtitle && (
          <Typography variant="caption" color="text.secondary">
            {subtitle}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
}

// ─── Tab Panel ───────────────────────────────────────────────────────────────

function TabPanel({
  children,
  value,
  index,
}: {
  children: React.ReactNode;
  value: number;
  index: number;
}) {
  return (
    <Box role="tabpanel" hidden={value !== index} sx={{ pt: 2 }}>
      {value === index && children}
    </Box>
  );
}

// ─── CSV download helper ─────────────────────────────────────────────────────

function downloadCsv(csvContent: string, filename: string) {
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

// ─── API helper ──────────────────────────────────────────────────────────────

async function apiFetch<T>(
  endpoint: string,
  params: Record<string, string | number | boolean>
): Promise<T> {
  const query = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== '' && v !== undefined && v !== null) {
      query.set(k, String(v));
    }
  }
  const url = `/api/vin-inventory/${endpoint}?${query.toString()}`;
  const res = await fetch(url);
  if (!res.ok) {
    const err = await res.json().catch(() => ({ detail: res.statusText }));
    throw new Error(err.detail || `API error ${res.status}`);
  }
  return res.json();
}

// =============================================================================
// Tab 1: Enrolled VINs
// =============================================================================

function EnrolledVinsTab() {
  const [data, setData] = useState<EnrolledRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(100);
  const [summary, setSummary] = useState<any>(null);
  const [csvContent, setCsvContent] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const fetched = useRef(false);

  // Filters
  const [vinFilter, setVinFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [enrollmentFilter, setEnrollmentFilter] = useState('');
  const [makeFilter, setMakeFilter] = useState('');
  const [modelFilter, setModelFilter] = useState('');
  const [platformFilter, setPlatformFilter] = useState('');
  const [packageFilter, setPackageFilter] = useState('');
  const [pkgStatusFilter, setPkgStatusFilter] = useState('');
  const [connectionFilter, setConnectionFilter] = useState('');
  const [deletedFilter, setDeletedFilter] = useState('false');
  const [deleteSourceFilter, setDeleteSourceFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  // Count active (non-default) filters for the badge
  const activeFilterCount = [
    vinFilter,
    statusFilter,
    enrollmentFilter,
    makeFilter,
    modelFilter,
    platformFilter,
    packageFilter,
    pkgStatusFilter,
    connectionFilter,
    deletedFilter,
    deleteSourceFilter,
    dateFrom,
    dateTo,
  ].filter(Boolean).length;

  const fetchData = useCallback(
    async (pageNum = page, perPage = rowsPerPage) => {
      setLoading(true);
      setError(null);
      try {
        const result = await apiFetch<any>('enrolled-vins', {
          limit: perPage,
          offset: pageNum * perPage,
          ...(vinFilter && { vin: vinFilter }),
          ...(statusFilter && { status: statusFilter }),
          ...(enrollmentFilter && { enrollment_status: enrollmentFilter }),
          ...(makeFilter && { make: makeFilter }),
          ...(modelFilter && { model: modelFilter }),
          ...(platformFilter && { platform: platformFilter }),
          ...(packageFilter && { package: packageFilter }),
          ...(pkgStatusFilter && { package_status: pkgStatusFilter }),
          ...(connectionFilter && { tcu_type: connectionFilter }),
          ...(deleteSourceFilter && { delete_source: deleteSourceFilter }),
          ...(deletedFilter === 'true' && { deleted_only: true, include_deleted: true }),
          ...(deletedFilter === 'false' && { include_deleted: false }),
          ...(deletedFilter === '' && { include_deleted: true }),
          ...(dateFrom && { date_from: dateFrom }),
          ...(dateTo && { date_to: dateTo }),
        });
        setData(result.data || []);
        setTotalCount(result.total_count || 0);
        setSummary(result.summary || null);
        setCsvContent(result.csv_content || '');
      } catch (e: any) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    },
    [page, rowsPerPage, vinFilter, statusFilter, enrollmentFilter, makeFilter, modelFilter, platformFilter, packageFilter, pkgStatusFilter, connectionFilter, deletedFilter, deleteSourceFilter, dateFrom, dateTo]
  );

  useEffect(() => {
    if (!fetched.current) {
      fetched.current = true;
      fetchData(0, rowsPerPage);
    }
  }, [fetchData, rowsPerPage]);

  const handleSearch = () => {
    setPage(0);
    fetchData(0, rowsPerPage);
  };

  const columns = [
    'VIN',
    'Platform',
    'Make',
    'Model',
    'Package',
    'Pkg Status',
    'Vehicle Status',
    'Enrollment',
    'Connection',
    'Deleted',
    'Delete Source',
    'Enrolled',
    'Last Updated',
  ];

  return (
    <Box>
      {/* Summary — Total VINs only */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <SummaryCard title="Total VINs" value={totalCount} color="#2E77D0" />
      </Box>

      {/* Toolbar */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, flexWrap: 'wrap' }}>
        <Button
          size="small"
          variant={activeFilterCount > 0 ? 'contained' : 'outlined'}
          color={activeFilterCount > 0 ? 'primary' : 'inherit'}
          startIcon={<FilterListIcon />}
          onClick={() => setShowFilters((p) => !p)}
        >
          Filters{activeFilterCount > 0 ? ` (${activeFilterCount} applied)` : ''}
        </Button>
        <Button
          size="small"
          variant="contained"
          startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <RefreshIcon />}
          onClick={handleSearch}
          disabled={loading}
        >
          {loading ? 'Loading...' : 'Refresh'}
        </Button>
        {csvContent && (
          <Button size="small" variant="outlined" startIcon={<DownloadIcon />} onClick={() => downloadCsv(csvContent, 'enrolled_vins.csv')}>
            Export CSV
          </Button>
        )}
      </Box>

      {/* Filters */}
      <Collapse in={showFilters}>
        <Card sx={{ mb: 2, p: 2 }}>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center', mb: 2 }}>
            <TextField size="small" label="VIN" placeholder="e.g. 5N1BT3BA..." value={vinFilter} onChange={(e) => setVinFilter(e.target.value.toUpperCase())} sx={{ width: 200 }} />
            <TextField size="small" label="Make" placeholder="e.g. NISSAN" value={makeFilter} onChange={(e) => setMakeFilter(e.target.value.toUpperCase())} sx={{ width: 140 }} />
            <TextField size="small" label="Model" placeholder="e.g. ROGUE" value={modelFilter} onChange={(e) => setModelFilter(e.target.value.toUpperCase())} sx={{ width: 160 }} />
            <FormControl size="small" sx={{ width: 140 }}>
              <InputLabel>Platform</InputLabel>
              <Select value={platformFilter} label="Platform" onChange={(e) => setPlatformFilter(e.target.value)}>
                <MenuItem value="">All</MenuItem>
                {summary?.by_platform && Object.keys(summary.by_platform).sort().map((p) => (
                  <MenuItem key={p} value={p}>{p}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ width: 200 }}>
              <InputLabel>Package</InputLabel>
              <Select value={packageFilter} label="Package" onChange={(e) => setPackageFilter(e.target.value)}>
                <MenuItem value="">All</MenuItem>
                {summary?.by_package && Object.keys(summary.by_package).sort().map((pkg) => (
                  <MenuItem key={pkg} value={pkg}>{pkg}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ width: 150 }}>
              <InputLabel>Pkg Status</InputLabel>
              <Select value={pkgStatusFilter} label="Pkg Status" onChange={(e) => setPkgStatusFilter(e.target.value)}>
                <MenuItem value="">All</MenuItem>
                {summary?.by_package_status && Object.keys(summary.by_package_status).sort().map((s) => (
                  <MenuItem key={s} value={s}>{s}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
            <FormControl size="small" sx={{ width: 180 }}>
              <InputLabel>Vehicle Status</InputLabel>
              <Select value={statusFilter} label="Vehicle Status" onChange={(e) => setStatusFilter(e.target.value)}>
                <MenuItem value="">All</MenuItem>
                {summary?.by_status && Object.keys(summary.by_status).map((s) => (
                  <MenuItem key={s} value={s}>{s}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ width: 200 }}>
              <InputLabel>Enrollment</InputLabel>
              <Select value={enrollmentFilter} label="Enrollment" onChange={(e) => setEnrollmentFilter(e.target.value)}>
                <MenuItem value="">All</MenuItem>
                {summary?.by_enrollment && Object.keys(summary.by_enrollment).map((s) => (
                  <MenuItem key={s} value={s}>{s}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ width: 150 }}>
              <InputLabel>Connection</InputLabel>
              <Select value={connectionFilter} label="Connection" onChange={(e) => setConnectionFilter(e.target.value)}>
                <MenuItem value="">All</MenuItem>
                {summary?.by_connection && Object.keys(summary.by_connection).sort().map((c) => (
                  <MenuItem key={c} value={c}>{c}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ width: 140 }}>
              <InputLabel>Deleted</InputLabel>
              <Select value={deletedFilter} label="Deleted" onChange={(e) => setDeletedFilter(e.target.value)}>
                <MenuItem value="">All</MenuItem>
                <MenuItem value="true">Yes</MenuItem>
                <MenuItem value="false">No</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ width: 160 }}>
              <InputLabel>Delete Source</InputLabel>
              <Select value={deleteSourceFilter} label="Delete Source" onChange={(e) => setDeleteSourceFilter(e.target.value)}>
                <MenuItem value="">All</MenuItem>
                {summary?.by_delete_source && Object.keys(summary.by_delete_source).sort().map((ds) => (
                  <MenuItem key={ds} value={ds}>{ds}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField size="small" label="Created From" type="date" InputLabelProps={{ shrink: true }} value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} sx={{ width: 150 }} />
            <TextField size="small" label="Created To" type="date" InputLabelProps={{ shrink: true }} value={dateTo} onChange={(e) => setDateTo(e.target.value)} sx={{ width: 150 }} />
            <Button variant="contained" size="small" startIcon={<SearchIcon />} onClick={handleSearch}>
              Search
            </Button>
          </Box>
        </Card>
      </Collapse>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <TableContainer component={Paper} sx={{ maxHeight: 'calc(100vh - 380px)' }}>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              {columns.map((col) => (
                <TableCell key={col} sx={{ fontWeight: 700, backgroundColor: '#f5f7fa', whiteSpace: 'nowrap', fontSize: '0.8rem' }}>
                  {col}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && data.length === 0 ? (
              <TableRow>
                <TableCell colSpan={columns.length} align="center" sx={{ py: 6 }}>
                  <CircularProgress size={32} />
                </TableCell>
              </TableRow>
            ) : data.length === 0 ? (
              <TableRow>
                <TableCell colSpan={columns.length} align="center" sx={{ py: 6 }}>
                  <Typography color="text.secondary">No data. Click Refresh to load.</Typography>
                </TableCell>
              </TableRow>
            ) : (
              data.map((row, i) => (
                <TableRow key={row.vin + i} hover>
                  <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.78rem' }}>{row.vin}</TableCell>
                  <TableCell>{row.platform}</TableCell>
                  <TableCell>{row.make}</TableCell>
                  <TableCell>{row.model_name}</TableCell>
                  <TableCell>{row.package}</TableCell>
                  <TableCell><StatusChip value={row.package_status} /></TableCell>
                  <TableCell><StatusChip value={row.vehicle_status} /></TableCell>
                  <TableCell><StatusChip value={row.enrollment_status} /></TableCell>
                  <TableCell>{row.connection_capability}</TableCell>
                  <TableCell><StatusChip value={row.deleted_flag} /></TableCell>
                  <TableCell>{row.source_of_delete}</TableCell>
                  <TableCell sx={{ fontSize: '0.75rem', whiteSpace: 'nowrap' }}>{row.enrollment_date}</TableCell>
                  <TableCell sx={{ fontSize: '0.75rem', whiteSpace: 'nowrap' }}>{row.last_updated}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={totalCount}
        page={page}
        onPageChange={(_, p) => { setPage(p); fetchData(p, rowsPerPage); }}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(e) => { const rpp = parseInt(e.target.value, 10); setRowsPerPage(rpp); setPage(0); fetchData(0, rpp); }}
        rowsPerPageOptions={[50, 100, 250, 500]}
      />
    </Box>
  );
}

// =============================================================================
// Main Page Component
// =============================================================================

export default function VinInventoryPage() {
  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <InventoryIcon fontSize="large" />
          VIN Inventory & Enrollment
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Hertz VIN data reports from MongoDB — enrollment, packages, and comparisons
        </Typography>
      </Box>

      {/* Tabs */}
      <Card>
        <Tabs
          value={0}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            borderBottom: 1,
            borderColor: 'divider',
            px: 2,
            '& .MuiTab-root': {
              textTransform: 'none',
              fontWeight: 600,
              minHeight: 48,
            },
          }}
        >
          <Tab icon={<ViewListIcon />} iconPosition="start" label="Enrolled VINs" />
        </Tabs>

        <Box sx={{ p: 2 }}>
          <TabPanel value={0} index={0}>
            <EnrolledVinsTab />
          </TabPanel>
        </Box>
      </Card>
    </Box>
  );
}
